import { Link } from 'react-aria-components';
import type { ReactElement } from 'react';
import { Wordmark } from '../components/Mark';
import { Icon } from '../ui/Icon';
import { sectionPath, type NavSection } from '../app/nav';
import { IconButton } from '../ui/IconButton';
import { AccountMenu } from './AccountMenu';
import { SectionIcon } from './SectionIcon';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';

// Icon then label: the same section icons the rail shows, so collapsing keeps every
// destination recognisable instead of swapping words for pictures.
const LINK =
  'flex items-center gap-3 px-3 py-2 rounded-control text-md font-ui no-underline text-on-nav-default hover:bg-on-nav-hover current:bg-fill current:text-on-fill focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-nav-strong';

/** Layout D's dark sidebar. The lists arrive as props; nothing here holds its own. */
export function Sidebar({
  workspaceId,
  sections,
  activeSection,
  onCollapse
}: {
  workspaceId: string;
  sections: NavSection[];
  activeSection: string | null;
  onCollapse(): void;
}): ReactElement {
  const link = (s: NavSection): ReactElement => (
    <Link
      key={s.id}
      href={sectionPath(s, workspaceId)}
      aria-current={s.id === activeSection ? 'page' : undefined}
      className={LINK}
    >
      <SectionIcon name={s.icon} />
      <span>{s.label}</span>
    </Link>
  );
  return (
    <nav aria-label="Main" className="w-sidebar bg-nav text-on-nav-default font-ui flex flex-col gap-1 p-3 sticky top-0 h-screen overflow-y-auto">
      <div className="flex items-center justify-between gap-2">
        {sections[0] ? (
          <Link href={sectionPath(sections[0], workspaceId)} aria-label="ReportPad home" className="no-underline text-on-nav-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-nav-strong">
            <Wordmark tone="dark" />
          </Link>
        ) : (
          <Wordmark tone="dark" />
        )}
        <IconButton label="Collapse sidebar" surface="dark" onPress={onCollapse}>
          <Icon name="menu" size={16} />
        </IconButton>
      </div>
      <WorkspaceSwitcher workspaceId={workspaceId} variant="full" />
      {sections.filter((s) => s.placement === 'main').map(link)}
      <div className="grow" />
      {sections.filter((s) => s.placement === 'bottom').map(link)}
      <AccountMenu />
    </nav>
  );
}
